/**
 * Dashboard — account / licence information panel.
 *
 * Shows License Type, Expiry Date (with days-remaining countdown),
 * and App Version. Clean three-card layout with interactive hover states.
 */
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  HiOutlineDocumentText,
  HiOutlineCalendar,
  HiOutlineCube,
} from "react-icons/hi";
import { getColorTheme } from "@service/local-storage-handler";
import "./Dashboard.scss";

type ExpiryUrgency = "ok" | "warning" | "critical" | "expired";

interface ExpiryInfo {
  dateStr: string;
  countdownStr: string;
  diffDays: number;
  urgency: ExpiryUrgency;
}

const LicenseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const VersionIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Dashboard: React.FC = () => {
  const themeFromStore = useSelector(({ theme }: any) => theme.theme);
  const theme = themeFromStore || getColorTheme() || "dark";
  const { dashboardState } = useSelector(({ metadata }: any) => metadata);

  const appVersion = process.env.REACT_APP_VERSION || "1.0.0";

  /* ── Expiry calculation ───────────────────────────────── */
  const expiryInfo = useMemo((): ExpiryInfo | null => {
    if (!dashboardState.licenseExpiryDt) return null;
    try {
      const expiry = new Date(dashboardState.licenseExpiryDt);
      const now = new Date();
      const expiryDay = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
      const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffDays = Math.round((expiryDay.getTime() - nowDay.getTime()) / 86400000);

      const dd = String(expiry.getDate()).padStart(2, "0");
      const mm = String(expiry.getMonth() + 1).padStart(2, "0");
      const yyyy = expiry.getFullYear();
      const dateStr = `${dd}-${mm}-${yyyy}`;

      let countdownStr: string;
      let urgency: ExpiryUrgency;

      if (diffDays < 0) {
        countdownStr = `expired ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""} ago`;
        urgency = "expired";
      } else if (diffDays === 0) {
        countdownStr = "expires today";
        urgency = "critical";
      } else if (diffDays === 1) {
        countdownStr = "1 day remaining";
        urgency = "critical";
      } else if (diffDays <= 7) {
        countdownStr = `${diffDays} days remaining`;
        urgency = "warning";
      } else {
        countdownStr = `${diffDays} days remaining`;
        urgency = "ok";
      }

      return { dateStr, countdownStr, diffDays, urgency };
    } catch {
      return null;
    }
  }, [dashboardState.licenseExpiryDt]);

  /* ── License label ────────────────────────────────────── */
  const licenseLabel = dashboardState.licenseType
    ? dashboardState.licenseType.charAt(0).toUpperCase() +
      dashboardState.licenseType.slice(1).toLowerCase()
    : "—";

  const isDemo = dashboardState.licenseType?.toLowerCase() === "demo";

  return (
    <div className={`db-root ${theme}`}>
      {/* ── Background glow ── */}
      <div className="db-bg-glow db-bg-glow--left" />
      <div className="db-bg-glow db-bg-glow--right" />

      {/* ── Header ── */}
      <header className="db-header">
        <p className="db-header__greeting">Welcome back</p>
        <h1 className="db-header__name">
          {dashboardState.userName || "User"}
        </h1>
        <p className="db-header__sub">Account Overview</p>
        <div className="db-header__divider" />
      </header>

      {/* ── Cards ── */}
      <div className="db-cards">

        {/* License Type */}
        <div className={`db-card db-card--license${isDemo ? " db-card--demo" : ""}`}>
          <div className="db-card__top-bar" />
          <div className="db-card__inner">
            <div className="db-card__icon-wrap db-card__icon-wrap--license">
              <LicenseIcon />
            </div>
            <div className="db-card__body">
              <span className="db-card__label">License Type</span>
              <span className="db-card__value">{licenseLabel}</span>
            </div>
          </div>
          <span className={`db-card__badge ${isDemo ? "db-card__badge--demo" : "db-card__badge--active"}`}>
            {isDemo ? "DEMO" : "ACTIVE"}
          </span>
          <div className="db-card__glare" />
        </div>

        {/* Expiry Date */}
        <div className={`db-card db-card--expiry${expiryInfo ? ` db-card--expiry-${expiryInfo.urgency}` : ""}`}>
          <div className="db-card__top-bar" />
          <div className="db-card__inner">
            <div className={`db-card__icon-wrap db-card__icon-wrap--expiry${expiryInfo ? ` db-card__icon-wrap--expiry-${expiryInfo.urgency}` : ""}`}>
              <CalendarIcon />
            </div>
            <div className="db-card__body">
              <span className="db-card__label">Expiry Date</span>
              {expiryInfo ? (
                <>
                  <span className="db-card__value">{expiryInfo.dateStr}</span>
                  <span className={`db-card__countdown db-card__countdown--${expiryInfo.urgency}`}>
                    {expiryInfo.urgency !== "expired" && (
                      <span className="db-card__dot db-card__dot--pulse" />
                    )}
                    {expiryInfo.countdownStr}
                  </span>
                </>
              ) : (
                <span className="db-card__value db-card__value--muted">—</span>
              )}
            </div>
          </div>
          <div className="db-card__glare" />
        </div>

        {/* App Version */}
        <div className="db-card db-card--version">
          <div className="db-card__top-bar" />
          <div className="db-card__inner">
            <div className="db-card__icon-wrap db-card__icon-wrap--version">
              <VersionIcon />
            </div>
            <div className="db-card__body">
              <span className="db-card__label">App Version</span>
              <span className="db-card__value db-card__value--mono">v{appVersion}</span>
            </div>
          </div>
          <span className="db-card__badge db-card__badge--version">LATEST</span>
          <div className="db-card__glare" />
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
